import React from "react";
import Select from "react-select";
import { BaseEditor } from "handsontable/editors";
import * as ReactDOM from "react-dom/client";
import { getOptions } from "../utils/getOptions";
import { colorSchemes } from "../config/colorSchemes";

// Custom React Select Editor
export class SelectEditor extends BaseEditor {
  constructor(hotInstance) {
    super(hotInstance);

    // Create an editorContainer - a detached DOM element
    this.editorContainer = document.createElement("div");
    this.editorContainer.style.position = "absolute";
    this.editorContainer.style.zIndex = 999;

    // Initialize value
    this.value = null;
    this.options = [];
    this._isOpened = false;
    this.root = null;
    this.isLoading = false;
    this.url = null;
  }

  async prepare(row, col, prop, td, originalValue, cellProperties) {
    super.prepare(row, col, prop, td, originalValue, cellProperties);

    // Store the original value
    this.originalValue = originalValue;

    // Get options from cellProperties
    if (cellProperties.selectOptions) {
      this.options = cellProperties.selectOptions.map((option) => {
        if (typeof option === "object") {
          // Handle the new format with value and text properties
          if (option.hasOwnProperty("value") && option.hasOwnProperty("text")) {
            return { value: option.value, label: option.text };
          }
          return option;
        } else {
          return { value: option, label: option };
        }
      });
    }

    if (cellProperties.url && !cellProperties.asyncOptions) {
      this.url = cellProperties.url;
      await this.getOptionsFromUrl(cellProperties.url);
    }

    if (cellProperties.url) {
      this.url = cellProperties.url;
    }
  }

  async getOptionsFromUrl(url) {
    this.isLoading = true;
    this.options = await getOptions(url);
    this.isLoading = false;
  }

  getValue() {
    return this.value;
  }

  setValue(value) {
    this.value = value;
  }

  open() {
    this._isOpened = true;

    // Append the editor to the document body
    document.body.appendChild(this.editorContainer);

    // Position the editor over the cell
    const { left, top, width, height } = this.getEditedCellRect();

    // Set the container dimensions to match the cell
    this.editorContainer.style.left = `${left}px`;
    this.editorContainer.style.top = `${top}px`;
    this.editorContainer.style.width = `${width}px`;
    this.editorContainer.style.height = `${height}px`;

    // Create root only if it doesn't exist
    if (!this.root) {
      this.root = ReactDOM.createRoot(this.editorContainer);
    }

    // Render the React component
    this.root.render(this.renderComponent());
  }

  renderComponent() {
    // Find the selected option
    const selectedOption = this.options.find(
      (option) => option.value === this.originalValue
    );

    return (
      <Select
        options={this.options}
        value={selectedOption}
        onChange={this.handleChange}
        autoFocus
        menuIsOpen
        styles={this.getComponentStyles()}
        onBlur={this.close.bind(this)}
        onMenuClose={this.close.bind(this)}
        isClearable={true}
        isLoading={this.isLoading}
      />
    );
  }

  getComponentStyles() {
    const { height } = this.getEditedCellRect();

    const baseStyles = {
      control: (provided) => ({
        ...provided,
        minHeight: "initial",
        height: `${height}px`,
        borderRadius: 0,
        border: "none",
        boxShadow: "none",
        fontSize: "14px",
      }),
      menu: (provided) => ({
        ...provided,
        marginTop: 0,
        marginBottom: 0,
        fontSize: "14px",
      }),
      menuList: (provided) => ({
        ...provided,
        paddingTop: 0,
        paddingBottom: 0,
        fontSize: "14px",
      }),
      option: (provided, state) => ({
        ...provided,
        padding: "4px 8px",
        fontSize: "14px",
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: "0 8px",
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        padding: 0,
      }),
      singleValue: (provided, state) => ({
        ...provided,
        fontSize: "14px",
      }),
    };

    const colorSchemeKey = this.cellProperties.data;
    const currentColorScheme = colorSchemeKey
      ? colorSchemes[colorSchemeKey]
      : null;
    const styles = currentColorScheme
      ? this.createColoredStyles(currentColorScheme)
      : baseStyles;

    return styles;
  }

  close() {
    if (this._isOpened) {
      this._isOpened = false;

      // Unmount the React component by rendering null
      if (this.root) {
        this.root.render(null);
      }

      // Remove the editor from the DOM
      if (this.editorContainer.parentNode) {
        this.editorContainer.parentNode.removeChild(this.editorContainer);
      }

      // Call super.finishEditing() to properly clean up
      super.finishEditing();
    }
  }

  focus() {}

  beginEditing() {
    super.beginEditing();
    this.open();
  }

  finishEditing() {
    super.finishEditing();
  }

  handleChange = (selectedOption) => {
    // Set the value
    this.value = selectedOption ? selectedOption.value : null;

    // Update the cell value directly
    const { row, col } = this;

    // Only update if the editor is still active
    if (this.hot) {
      this.hot.setDataAtCell(row, col, this.value);
    }

    // Now close the editor
    this.close();
  };

  getEditedCellRect() {
    const TD = this.getEditedCell();
    const { top, left, width, height } = TD.getBoundingClientRect();

    return {
      top: top + window.pageYOffset,
      left: left + window.pageXOffset,
      width,
      height,
    };
  }

  isOpened() {
    return this._isOpened;
  }

  // Clean up when the editor instance is destroyed
  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.editorContainer.parentNode) {
      this.editorContainer.parentNode.removeChild(this.editorContainer);
    }

    super.destroy();
  }

  createColoredStyles(colorScheme) {
    const { height } = this.getEditedCellRect();

    return {
      control: (provided) => ({
        ...provided,
        minHeight: "initial",
        height: `${height}px`,
        borderRadius: 0,
        border: "none",
        boxShadow: "none",
        fontSize: "14px",
      }),
      menu: (provided) => ({
        ...provided,
        marginTop: 0,
        marginBottom: 0,
        fontSize: "14px",
      }),
      menuList: (provided) => ({
        ...provided,
        paddingTop: 0,
        paddingBottom: 0,
        fontSize: "14px",
      }),
      option: (provided, state) => {
        const color = colorScheme?.[state.data.value];
        if (!color)
          return {
            ...provided,
            padding: "4px 8px",
            fontSize: "14px",
          };

        return {
          ...provided,
          padding: "4px 8px",
          fontSize: "14px",
          backgroundColor: state.isFocused ? `${color[0]}50` : color[0],
          color: state.isFocused ? "#000000" : color[1],
          ":active": {
            backgroundColor: color[0],
            color: color[1],
            fontSize: "14px",
          },
        };
      },
      valueContainer: (provided, state) => ({
        ...provided,
        padding: "0 8px",
        backgroundColor:
          colorScheme?.[state.getValue()?.[0]?.value]?.[0] ||
          provided.backgroundColor,
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        padding: 0,
      }),
      singleValue: (provided, state) => ({
        ...provided,
        fontSize: "14px",
        color: colorScheme?.[state.data.value]?.[1] || provided.color,
        backgroundColor:
          colorScheme?.[state.data.value]?.[0] || provided.backgroundColor,
      }),
    };
  }
}
